/**
 * XlsxErrorList — reusable per-row error panel for XLSX importers.
 *
 * Both ImportDialog (xlsx mode) and the Create-Test wizard's
 * ParticipantsStep render the same error shape returned by the backend's
 * importer modules — this component is the single home for that
 * presentation.
 */

import type { ReferenceImpact } from "../../services/apiClient";

export interface XlsxRowError {
  row: number;
  code: string;
  message: string;
  field?: string;
  sample_value?: string;
}

interface Props {
  errors: XlsxRowError[];
  /** Headline above the list. Defaults to a generic count. */
  title?: string;
  /** Cap the list — beyond this we render an overflow line. Default 100. */
  maxVisible?: number;
}

export function XlsxErrorList({ errors, title, maxVisible = 100 }: Props) {
  if (errors.length === 0) return null;
  const visible = errors.slice(0, maxVisible);
  const overflow = errors.length - visible.length;
  return (
    <div>
      <p className="text-xs font-semibold text-brand-steel uppercase tracking-wider mb-2">
        {title ?? `Validation errors (${errors.length})`}
      </p>
      <div className="p-3 rounded-lg border border-brand-error/40 bg-red-50 max-h-64 overflow-y-auto">
        <ul className="text-xs text-brand-error space-y-1">
          {visible.map((e, i) => (
            <li key={i}>
              <span className="font-medium">Row {e.row}:</span>{" "}
              {e.field ? <span className="font-mono">[{e.field}] </span> : null}
              {e.message}
              {e.sample_value ? (
                <span className="text-brand-error/80"> &mdash; got &quot;{e.sample_value}&quot;</span>
              ) : null}
            </li>
          ))}
          {overflow > 0 && (
            <li className="text-brand-error/70">… {overflow} more</li>
          )}
        </ul>
      </div>
    </div>
  );
}

// Re-export the type alias so upstream callers don't need to import the
// wider apiClient module just for the impact-list shape.
export type { ReferenceImpact };

export default XlsxErrorList;
